import { IStringTool } from "../core/IStringTool"
import { StringColor } from "../models/color/StringColor"

/**
 * Input for parsing color strings into RGBA format.
 */
interface ColorParserInput {
  /** Color string in hex, rgb[a], or hsl[a] format. */
  value: string
}

/**
 * Parses a CSS color string (`#fff`, `rgb(...)`, `hsl(...)`, etc.)
 * into an object with `r`, `g`, `b`, `a` values.
 */
export default class ColorParserTool
  implements IStringTool<ColorParserInput, StringColor>
{
  /**
   * @returns RGBA object parsed from color string.
   */
  process({ value }: ColorParserInput): StringColor {
    const str = value.trim().toLowerCase()

    // --- HEX ---
    if (str.startsWith("#")) {
      let hex = str.slice(1)

      if (hex.length === 3) {
        hex = hex.split("").map((ch) => ch + ch).join("")
      }

      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1

      return { r, g, b, a }
    }

    // --- RGB / RGBA ---
    const rgbMatch = str.match(/rgba?\(([^)]+)\)/)
    if (rgbMatch) {
      const [r, g, b, a = 1] = rgbMatch[1]
        .split(",")
        .map((v) => parseFloat(v.trim()))

      return { r, g, b, a }
    }

    // --- HSL / HSLA ---
    const hslMatch = str.match(/hsla?\(([^)]+)\)/)
    if (hslMatch) {
      const [h, s, l, a = "1"] = hslMatch[1].split(",").map((v) => v.trim())
      const [r, g, b] = this.hslToRgb(parseFloat(h), parseFloat(s), parseFloat(l))
      return { r, g, b, a: parseFloat(a) }
    }

    // fallback: transparent
    return { r: 0, g: 0, b: 0, a: 0 }
  }

  private hslToRgb(h: number, s: string | number, l: string | number): [number, number, number] {
    h = h / 360
    s = parseFloat(s.toString()) / 100
    l = parseFloat(l.toString()) / 100

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255)
    const g = Math.round(hue2rgb(p, q, h) * 255)
    const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255)

    return [r, g, b]
  }
}
